import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { theme, Typography } from "antd";
import {
  normalizeRichContent,
  signImagesInHtml,
  patchCodeBlocksFromDelta,
} from "../utils/richContent";
import InlineVideoEmbed from "./InlineVideoEmbed";
import InlineModel3DEmbed from "./InlineModel3DEmbed";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "u", "s", "blockquote", "pre", "code",
    "ol", "ul", "li",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "a", "img", "iframe", "span", "div", "hr", "button",
    "table", "thead", "tbody", "tr", "th", "td",
  ],
  ALLOWED_ATTR: [
    "href", "target", "rel", "src", "alt", "title", "class", "style",
    "frameborder", "allow", "allowfullscreen", "width", "height",
    "data-list",
    "data-airsmart-embed", "data-media-id", "data-action",
    "data-language",
    "contenteditable", "type",
  ],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ["target"],
};

/**
 * Split sanitized HTML at every top-level [data-airsmart-embed] element so
 * each embed renders as a normal React child. Avoids portal-into-innerHTML
 * timing races we previously hit.
 */
function splitContentIntoSegments(html) {
  if (!html || typeof html !== "string") return [];
  if (typeof document === "undefined") return [{ type: "html", value: html }];
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  const segments = [];
  let buffer = "";
  const flush = () => {
    if (buffer) {
      segments.push({ type: "html", value: buffer });
      buffer = "";
    }
  };
  Array.from(wrapper.childNodes).forEach((node) => {
    if (
      node.nodeType === 1 &&
      node.hasAttribute &&
      node.hasAttribute("data-airsmart-embed") &&
      node.hasAttribute("data-media-id")
    ) {
      flush();
      segments.push({
        type: "embed",
        kind: node.getAttribute("data-airsmart-embed"),
        mediaId: node.getAttribute("data-media-id"),
      });
    } else if (node.nodeType === 1) {
      buffer += node.outerHTML;
    } else if (node.nodeType === 3) {
      buffer += node.textContent || "";
    }
  });
  flush();
  return segments;
}

/**
 * RichContentViewer — sanitized HTML render for Quill content with inline
 * topic-media embeds (video, 3D model). Also patches code-block content from
 * delta so indentation / whitespace survives the HTML round-trip.
 */
const RichContentViewer = ({
  content,
  compact = false,
  maxHeight = null,
  emptyText = "",
  className,
  style,
  mediaResolver,
  onOpenFullscreen3D,
}) => {
  const { token } = theme.useToken();
  const normalized = useMemo(() => normalizeRichContent(content), [content]);

  const safeHtml = useMemo(() => {
    if (!normalized.html) return "";
    // Patch code block content from delta (HTML field loses whitespace/indent).
    const patched = patchCodeBlocksFromDelta(normalized.html, normalized.delta);
    const signed = signImagesInHtml(patched);
    return DOMPurify.sanitize(signed, SANITIZE_CONFIG);
  }, [normalized.html, normalized.delta]);

  const segments = useMemo(
    () => splitContentIntoSegments(safeHtml),
    [safeHtml],
  );

  if (!safeHtml) {
    if (!emptyText) return null;
    return (
      <Typography.Text
        style={{ color: token.colorTextTertiary, fontStyle: "italic" }}
      >
        {emptyText}
      </Typography.Text>
    );
  }

  const fontFamily =
    "var(--font-untitled-sans), -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const fontSize = compact ? 14 : 16;
  const lineHeight = compact ? 1.7 : 1.85;

  return (
    <div
      className={`airsmart-rich-content ${className || ""}`}
      style={{
        color: token.colorText,
        fontFamily,
        fontSize,
        lineHeight,
        wordBreak: "break-word",
        ...(maxHeight ? { maxHeight, overflowY: "auto", paddingRight: 8 } : {}),
        ...style,
      }}
    >
      <style>{`
        /* Reset global brand overrides inside rich content */
        .airsmart-rich-content h1,
        .airsmart-rich-content h2,
        .airsmart-rich-content h3,
        .airsmart-rich-content h4,
        .airsmart-rich-content h5,
        .airsmart-rich-content h6 {
          text-transform: none !important;
          letter-spacing: normal !important;
          font-family: inherit !important;
          font-weight: 600 !important;
          line-height: 1.3 !important;
        }
        .airsmart-rich-content p { margin: ${compact ? "4px 0" : "10px 0"}; color: ${token.colorTextSecondary}; }
        .airsmart-rich-content p:first-child { margin-top: 0; }
        .airsmart-rich-content p:last-child { margin-bottom: 0; }
        /* Empty paragraphs (blank lines) — preserve visual height */
        .airsmart-rich-content p:empty,
        .airsmart-rich-content p > br:only-child { min-height: 1.4em; display: block; }
        .airsmart-rich-content h1 { font-size: ${compact ? 20 : 26}px !important; font-weight: 600 !important; margin: 16px 0 8px !important; color: ${token.colorText} !important; }
        .airsmart-rich-content h2 { font-size: ${compact ? 18 : 22}px !important; font-weight: 600 !important; margin: 14px 0 8px !important; color: ${token.colorText} !important; }
        .airsmart-rich-content h3 { font-size: ${compact ? 16 : 19}px !important; font-weight: 600 !important; margin: 12px 0 6px !important; color: ${token.colorText} !important; }
        .airsmart-rich-content h4 { font-size: ${compact ? 15 : 17}px !important; font-weight: 600 !important; margin: 10px 0 4px !important; color: ${token.colorText} !important; }
        /* Quill alignment classes */
        .airsmart-rich-content .ql-align-center { text-align: center !important; }
        .airsmart-rich-content .ql-align-right  { text-align: right !important; }
        .airsmart-rich-content .ql-align-justify { text-align: justify !important; }
        /* Quill indent classes */
        .airsmart-rich-content .ql-indent-1 { padding-left: 3em !important; }
        .airsmart-rich-content .ql-indent-2 { padding-left: 6em !important; }
        .airsmart-rich-content .ql-indent-3 { padding-left: 9em !important; }
        .airsmart-rich-content .ql-indent-4 { padding-left: 12em !important; }
        .airsmart-rich-content .ql-indent-5 { padding-left: 15em !important; }
        .airsmart-rich-content ul, .airsmart-rich-content ol { padding-left: 24px; margin: ${compact ? "6px 0" : "10px 0"}; color: ${token.colorTextSecondary}; }
        .airsmart-rich-content li { margin-bottom: 4px; list-style-position: outside; }
        /* Quill v2 list rendering — li[data-list] instead of standard ul/ol children */
        .airsmart-rich-content li[data-list="bullet"] { list-style-type: disc; }
        .airsmart-rich-content li[data-list="ordered"] { list-style-type: decimal; }
        .airsmart-rich-content li[data-list="checked"] { list-style-type: none; }
        .airsmart-rich-content li[data-list="unchecked"] { list-style-type: none; }
        /* Hide Quill's .ql-ui pseudo-content spans (editor-only UI) */
        .airsmart-rich-content .ql-ui { display: none; }
        .airsmart-rich-content blockquote {
          border-left: 4px solid ${token.colorPrimary};
          background: ${token.colorBgContainer};
          padding: 12px 16px;
          margin: 14px 0;
          border-radius: 6px;
          color: ${token.colorTextSecondary};
          border: 1px solid ${token.colorBorderSecondary};
          border-left-width: 4px;
          border-left-color: ${token.colorPrimary};
        }
        .airsmart-rich-content pre {
          background: ${token.colorFillTertiary};
          color: ${token.colorText};
          border-radius: 8px;
          padding: 14px 16px;
          font-family: Menlo, Consolas, 'Courier New', monospace;
          font-size: ${compact ? 13 : 14}px;
          overflow-x: auto;
          margin: 12px 0;
          white-space: pre !important;
          tab-size: 4;
          -moz-tab-size: 4;
        }
        .airsmart-rich-content code {
          background: ${token.colorFillSecondary};
          padding: 2px 6px;
          border-radius: 4px;
          font-family: Menlo, Consolas, 'Courier New', monospace;
          font-size: 0.92em;
        }
        .airsmart-rich-content a {
          color: ${token.colorLink};
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .airsmart-rich-content a:hover { color: ${token.colorLinkHover}; }
        .airsmart-rich-content img {
          max-width: 100%;
          border-radius: 10px;
          margin: 12px 0;
          box-shadow: 0 2px 8px ${token.colorFillTertiary};
        }
        .airsmart-rich-content iframe {
          max-width: 100%;
          border-radius: 10px;
          border: 1px solid ${token.colorBorderSecondary};
        }
        .airsmart-rich-content hr {
          border: none;
          border-top: 1px solid ${token.colorBorderSecondary};
          margin: 16px 0;
        }
        .airsmart-rich-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 12px 0;
        }
        .airsmart-rich-content th, .airsmart-rich-content td {
          border: 1px solid ${token.colorBorderSecondary};
          padding: 10px 12px;
        }
        .airsmart-rich-content th {
          background: ${token.colorPrimaryBg};
          font-weight: 600;
          color: ${token.colorText};
        }
        .airsmart-rich-content strong { color: ${token.colorText}; }
      `}</style>
      {segments.map((seg, idx) => {
        if (seg.type === "embed") {
          const media = mediaResolver ? mediaResolver(seg.mediaId) : null;
          if (seg.kind === "video") {
            return (
              <InlineVideoEmbed
                key={`v-${seg.mediaId}-${idx}`}
                mediaId={seg.mediaId}
                media={media}
              />
            );
          }
          if (seg.kind === "model3d") {
            return (
              <InlineModel3DEmbed
                key={`m-${seg.mediaId}-${idx}`}
                mediaId={seg.mediaId}
                media={media}
                onOpenFullscreen={onOpenFullscreen3D}
              />
            );
          }
          return null;
        }
        return (
          <div
            key={`h-${idx}`}
            dangerouslySetInnerHTML={{ __html: seg.value }}
          />
        );
      })}
    </div>
  );
};

export default RichContentViewer;
